import axios, { AxiosInstance } from 'axios';
import { getEnvVar } from '../config/env';

export type Program7Response<T> = { success: boolean; data: T };

class Program7Service {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${getEnvVar('VITE_FASTAPI_BASE_URL')}/api/v1/admin/program7`,
      timeout: 60000,
      headers: { 'Content-Type': 'application/json' },
    });
    this.api.interceptors.request.use((config) => {
      const token =
        localStorage.getItem('admin_auth_token') ||
        document.cookie.split('; ').find((r) => r.startsWith('auth_token='))?.split('=')[1];
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  }

  async getStatus() {
    const { data } = await this.api.get<Program7Response<Record<string, unknown>>>('/status');
    return data;
  }

  async getDeploymentStatus() {
    const { data } = await this.api.get<Program7Response<Record<string, unknown>>>('/deployment/status');
    return data;
  }

  async startDeployment(strategy = 'rolling', targetVersion?: string) {
    const { data } = await this.api.post<Program7Response<Record<string, unknown>>>('/deployment/start', {
      strategy,
      target_version: targetVersion,
    });
    return data;
  }

  async approveDeployment(orchestrationId: string) {
    const { data } = await this.api.post<Program7Response<Record<string, unknown>>>(
      `/deployment/${encodeURIComponent(orchestrationId)}/approve`,
    );
    return data;
  }

  async advanceDrain(orchestrationId: string) {
    const { data } = await this.api.post<Program7Response<Record<string, unknown>>>(
      `/deployment/${encodeURIComponent(orchestrationId)}/advance-drain`,
    );
    return data;
  }

  async verifyDeployment(orchestrationId: string) {
    const { data } = await this.api.post<Program7Response<Record<string, unknown>>>(
      `/deployment/${encodeURIComponent(orchestrationId)}/verify`,
    );
    return data;
  }

  async rollbackDeployment(orchestrationId: string) {
    const { data } = await this.api.post<Program7Response<Record<string, unknown>>>(
      `/deployment/${encodeURIComponent(orchestrationId)}/rollback`,
    );
    return data;
  }

  async runEoatSuite(suiteKey: string) {
    const { data } = await this.api.post<Program7Response<Record<string, unknown>>>(
      `/eoat/suites/${encodeURIComponent(suiteKey)}/run`,
    );
    return data;
  }

  async generateReport(reportType: string) {
    const { data } = await this.api.post<Program7Response<Record<string, unknown>>>(
      `/certification/reports/${encodeURIComponent(reportType)}/generate`,
    );
    return data;
  }

  async validateSlos() {
    const { data } = await this.api.post<Program7Response<Record<string, unknown>>>('/certification/slo/validate');
    return data;
  }

  async getReadinessReport() {
    const { data } = await this.api.get<Program7Response<Record<string, unknown>>>('/readiness-report');
    return data;
  }

  async executePhysical(dryRun = true) {
    const { data } = await this.api.post<Program7Response<Record<string, unknown>>>('/physical/execute', { dry_run: dryRun });
    return data;
  }
}

export const program7Service = new Program7Service();
